"use client";
import { AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { twMerge } from "tailwind-merge";
import { Notice } from "./Notice";

interface NoticeData {
  id: string;
  message: string;
  variant: "success" | "error" | "warning" | "info";
  duration: number;
  dismissible: boolean;
}

export interface NoticeContainerProps {
  /** Array of notices to display */
  notices: NoticeData[];
  /** Callback when a notice is dismissed */
  onDismiss: (id: string) => void;
  /** Position of the container on the screen */
  position?: "top-left" | "top-center" | "top-right" | "bottom-left" | "bottom-center" | "bottom-right";
  /** Additional CSS classes */
  className?: string;
}

const positionStyles = {
  "top-left": "top-4 left-4 items-start",
  "top-center": "top-4 left-1/2 -translate-x-1/2 items-start",
  "top-right": "top-4 right-4 items-end",
  "bottom-left": "bottom-4 left-4 items-start",
  "bottom-center": "bottom-4 left-1/2 -translate-x-1/2 items-start",
  "bottom-right": "bottom-4 right-4 items-end",
};

/**
 * NoticeContainer
 *
 * Container component that positions and animates notices.
 * Renders notices in a portal to ensure they appear above other content.
 */
export function NoticeContainer({ notices, onDismiss, position = "bottom-center", className }: NoticeContainerProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  const container = (
    <div
      className={twMerge("pointer-events-none fixed z-50 flex flex-col gap-2", positionStyles[position], className)}
      aria-live="polite"
      aria-atomic="true"
    >
      <AnimatePresence mode="sync">
        {notices.map((notice) => (
          <Notice
            key={notice.id}
            id={notice.id}
            message={notice.message}
            variant={notice.variant}
            duration={notice.duration}
            dismissible={notice.dismissible}
            onDismiss={() => onDismiss(notice.id)}
          />
        ))}
      </AnimatePresence>
    </div>
  );

  // Portal to body to ensure notices appear above everything
  return createPortal(container, document.body);
}
